import React, { useState } from "react";
import api from "../../api";
import { fonts } from "../../theme";
import { Button, Field, inputStyle, ErrorBanner } from "../UI";

export default function RecruiterVerificationWizard({ onComplete, theme }) {
  const [vStep, setVStep] = useState(1);
  const [aadhaar, setAadhaar] = useState("");
  const [otp, setOtp] = useState("");
  const [gstNo, setGstNo] = useState("");
  const [company, setCompany] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const startAadhaar = async (e) => {
    e.preventDefault();
    if (!/^\d{12}$/.test(aadhaar)) {
      return setError("Please enter a valid 12-digit Aadhaar number.");
    }
    setError(""); setBusy(true);
    try {
      await api.post("/verify/aadhaar", { aadhaarNumber: aadhaar });
      setVStep(2);
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const verifyOtp = async (e) => {
    e.preventDefault();
    if (!otp) return setError("OTP is required.");
    setError(""); setBusy(true);
    try {
      await api.post("/verify/aadhaar/otp", { aadhaarNumber: aadhaar, otp });
      setVStep(3);
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const fetchGst = async (e) => {
    e.preventDefault();
    if (gstNo.trim().length !== 15) {
      return setError("Please enter a valid 15-character GST number.");
    }
    setError(""); setBusy(true);
    try {
      const data = await api.post("/verify/gst", { gstNo });
      setCompany(data);
      setVStep(4);
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const completeSetup = async () => {
    setError(""); setBusy(true);
    try {
      await api.post("/verify/verify-setup/recruiter", {
        aadhaarNumber: aadhaar,
        gstNo: company.gstNo,
        companyName: company.companyName,
        address: company.address
      });
      onComplete();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={{ maxWidth: 460, margin: "40px auto 0", padding: "0 20px" }}>
      <div style={{ background: theme.surface, border: `1px solid ${theme.line}`, borderRadius: 12, padding: 28 }}>
        <h3 style={{ fontFamily: fonts.display, fontSize: 20, fontWeight: 700, marginBottom: 6 }}>Verify Recruiter Account</h3>
        <p style={{ color: theme.textMute, fontSize: 13, marginBottom: 20 }}>To access candidates and execute screenings on WIBIL, verify your company details.</p>

        <ErrorBanner message={error} theme={theme} />

        {vStep === 1 && (
          <form onSubmit={startAadhaar}>
            <Field label="Recruiter Aadhaar Number (12 Digits)" theme={theme}>
              <input type="text" maxLength={12} required style={inputStyle(theme)} value={aadhaar} onChange={e => setAadhaar(e.target.value.replace(/\D/g, ""))} placeholder="000000000000" />
            </Field>
            <Button type="submit" theme={theme} style={{ width: "100%", justifyContent: "center" }} disabled={busy}>
              {busy ? "Sending OTP..." : "Send Verification OTP"}
            </Button>
          </form>
        )}

        {vStep === 2 && (
          <form onSubmit={verifyOtp}>
            <Field label="Enter Verification OTP (Use 123456 for testing)" theme={theme}>
              <input type="text" maxLength={6} required style={inputStyle(theme)} value={otp} onChange={e => setOtp(e.target.value.replace(/\D/g, ""))} placeholder="123456" />
            </Field>
            <div style={{ display: "flex", gap: 10, marginTop: 10 }}>
              <Button type="button" theme={{ ...theme, surface: theme.line }} onClick={() => setVStep(1)} style={{ flex: 1, justifyContent: "center", background: "transparent", border: `1px solid ${theme.line}`, color: theme.text }}>Back</Button>
              <Button type="submit" theme={theme} style={{ flex: 2, justifyContent: "center" }} disabled={busy}>
                {busy ? "Verifying OTP..." : "Verify OTP"}
              </Button>
            </div>
          </form>
        )}

        {vStep === 3 && (
          <form onSubmit={fetchGst}>
            <Field label="Company GSTIN (15 Characters)" theme={theme}>
              <input type="text" maxLength={15} required style={inputStyle(theme)} value={gstNo} onChange={e => setGstNo(e.target.value.toUpperCase())} placeholder="27ABCDE1234F1Z5" />
            </Field>
            <Button type="submit" theme={theme} style={{ width: "100%", justifyContent: "center" }} disabled={busy}>
              {busy ? "Fetching Company Info..." : "Verify GST & Fetch Company Name"}
            </Button>
          </form>
        )}

        {vStep === 4 && company && (
          <div>
            <div style={{ background: theme.bg, borderRadius: 8, padding: 16, marginBottom: 20, fontSize: 13 }}>
              <div style={{ marginBottom: 8 }}><strong>Company Name:</strong> {company.companyName}</div>
              <div style={{ marginBottom: 8 }}><strong>GSTIN Status:</strong> {company.gstNo}</div>
              <div style={{ marginBottom: 8 }}><strong>Company Location:</strong> {company.address}</div>
              <div><strong>Verification Status:</strong> <span style={{ color: theme.accent, fontWeight: 600 }}>{company.status}</span></div>
            </div>
            <div style={{ display: "flex", gap: 10 }}>
              <Button type="button" theme={{ ...theme, surface: theme.line }} onClick={() => setVStep(3)} style={{ flex: 1, justifyContent: "center", background: "transparent", border: `1px solid ${theme.line}`, color: theme.text }}>Back</Button>
              <Button type="button" theme={theme} onClick={completeSetup} style={{ flex: 2, justifyContent: "center" }} disabled={busy}>
                {busy ? "Activating Profile..." : "Confirm Company & Activate"}
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
